import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { AddCircleOutline, ExpandMore } from '@mui/icons-material'
import { forwardRef, useState, Fragment, useEffect } from 'react'
import { useGetOrderRequests } from '../../../logic/useApiQuery'
import { useCancelOrderRequest } from '../../../logic/useApiMutation'
import { useGetConfig } from '../../../logic/useApiUnauthedQuery'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { RetrieveOrderRequestBody, PersonInfo } from '../../../types/orderRequest'

const STATUS_COLORS: Record<string, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
  PENDING: 'warning',
  PROCESSING: 'info',
  ACCEPTED: 'success',
  REJECTED: 'error',
  ERROR: 'error',
  DRAFT: 'error',
  CANCELLED: 'default',
}

type OrderRequestRowProps = {
  req: RetrieveOrderRequestBody
  setSelectedRequest: React.Dispatch<React.SetStateAction<RetrieveOrderRequestBody | null>>
} & React.ComponentPropsWithoutRef<typeof TableRow>

const getOrderNumberDisplay = ({orderNum, status, prefix=""}: {orderNum?: string, status?: string, prefix?: string}) => {
const notAccepted = !!status && ['SUBMITTED', 'REJECTED', 'ERROR', 'DRAFT'].includes(status)
  if (notAccepted) {
    return (
      <Typography variant="overline">{(() => {
        if (status === 'REJECTED') return `${prefix}Order Rejected`
        if (status === 'SUBMITTED') return `${prefix}(Awaiting Acceptance)`
        if (status === 'ERROR' || status === 'DRAFT') return `${prefix}(Error in Processing)`
      })()
      }</Typography>
    )
  }
  return orderNum ? (<Typography variant="body2">{prefix}{orderNum}</Typography>) : <Typography>--</Typography>
}


function DetailField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

function DetailFieldFull({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <Box sx={{ mb: 1, gridColumn: '1 / -1' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

function RequestDetailDialog({ req, onClose, onCancel }: { req: RetrieveOrderRequestBody; onClose: () => void; onCancel: () => void;}) {
  const cancelMutation = useCancelOrderRequest(req.RequestId)
  const { data } = useGetConfig();
  const sendToOptions = data?.org?.OrderRequestFeature?.SendToOptions ?? []
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['transaction']))

  async function handleCancel() {
    await cancelMutation.mutateAsync(undefined)
    onCancel()
  }

  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <Accordion
      expanded={expanded.has(id)}
      onChange={() => toggle(id)}
      disableGutters
      elevation={0}
      sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
        <Typography variant="subtitle2" fontWeight="bold">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 0 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  )

  const BorrowerDetails = ({ party, subLabel }: { party: PersonInfo; subLabel?: string }) => {
    return party ? (
      <Box sx={{ mb: 1.5 }}>
        {subLabel && (
          <Typography variant="overline" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
            {subLabel}
          </Typography>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '0 24px' }}>
          <DetailField label="Name" value={[party.FirstName, party.MiddleName, party.LastName].filter(Boolean).join(' ')} />
          <DetailField label="Email" value={party.Email} />
          <DetailField label="Phone" value={party.Phone} />
        </Box>
      </Box>
    ) : <></>
  }

  const possibleParties = ["Buyer", "Seller"] as const

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
      <DialogTitle sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, pb: 1 }}>
        <Box>
          <Typography variant="h6">Order Request Details</Typography>
          <Box marginTop={1}>{getOrderNumberDisplay({orderNum: req.OrderNumber, status: req.Status, prefix: (req.OrderNumber ? "File Number: " : "")})}</Box>
          <Typography variant="caption" color="text.secondary">ID: {req.RequestId}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={req.Status} color={STATUS_COLORS[req.Status] ?? 'default'} size="small" />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>

        <Section id="transaction" title="Transaction Information">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '0 24px' }}>
            <DetailField label="Order Type" value={req.OrderType} />
            <DetailField label="Order Type Description" value={req.OtherOrderType} />
            <DetailField label="Closing Date" value={req.ClosingDate} />
            <DetailField label="Submitted" value={new Date(req.CreatedAt).toLocaleString()} />
            <DetailField label="Route To" value={sendToOptions.find((o) => o.id === req.RouteToId)?.name ?? req.RouteToId} />
          </Box>
        </Section>

        {req.PropertyInfo && (
          <Section id="property" title="Property Information">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '0 24px' }}>
              <DetailField label="Property Type" value={req.PropertyInfo.PropertyType} />
              <DetailFieldFull label="Address" value={[req.PropertyInfo.Address1, req.PropertyInfo.Address2].filter(Boolean).join(' ')} />
              <DetailField label="City" value={req.PropertyInfo.City} />
              <DetailField label="State / Zip" value={`${req.PropertyInfo.State} ${req.PropertyInfo.Zip}`} />
              <DetailField label="Tax ID" value={req.PropertyInfo.TaxId} />
              {/* <DetailField label="Vacant Land" value={req.PropertyInfo.PropertyType === 'Vacant Land' ? (req.PropertyInfo.VacantLand ? 'Yes' : 'No') : null} /> */}
              <DetailFieldFull label="Legal Description" value={req.PropertyInfo.LegalDescription} />
            </Box>
          </Section>
        )}

        {possibleParties.map((partyType) => {
          const parties = req.Parties?.[partyType]
          if (!parties?.length) return null
          return (
            <Section key={partyType} id={partyType.toLowerCase()} title={`${partyType}${parties.length > 1 ? 's' : ''}`}>
              {parties.map((b, i) => (
                <Fragment key={'party' + i}>
                  {/* Party-level header — always visible for clear nesting context */}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.75, mt: i > 0 ? 0 : 0 }}>
                    {parties.length > 1 && `${partyType} ${i + 1}`}
                  </Typography>
                  {/* Indent contact details under the party header */}
                  <Box sx={{ pl: 1.5, borderLeft: '3px solid', borderColor: 'divider' }}>
                    {b.Type === 'organization' ? (
                      <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '0 24px', mb: b.OrgPeople?.length ? 1 : 0 }}>
                          <DetailField label="Organization Name" value={b.OrganizationName} />
                          <DetailField label="Organization Type" value={b.OrganizationType} />
                        </Box>
                        {b.OrgPeople?.map((person, j) => (
                          <Box key={j} sx={{ mb: 1.5 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                              Contact {j + 1}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '0 24px' }}>
                              <DetailField label="Name" value={[person.FirstName, person.MiddleName, person.LastName].filter(Boolean).join(' ')} />
                              <DetailField label="Email" value={person.Email} />
                              <DetailField label="Phone" value={person.Phone} />
                            </Box>
                          </Box>
                        ))}
                      </>
                    ) : (
                      <>
                        {b.Primary && <BorrowerDetails party={b.Primary} subLabel={b.Spouse ? 'Primary' : undefined} />}
                        {b.Spouse && <BorrowerDetails party={b.Spouse} subLabel="Spouse" />}
                      </>
                    )}
                    {b.Address && (
                      <DetailField label="Contact Address" value={[b.Address.Address1, b.Address.Address2, b.Address.City, b.Address.State, b.Address.Zip].filter(Boolean).join(', ')} />
                    )}
                  </Box>
                  {i < parties.length - 1 && <Divider sx={{ my: 1.5 }} />}
                </Fragment>
              ))}
            </Section>
          )
        })}

        {!!req.Lenders?.length && (
          <Section id="lenders" title={req.Lenders.length > 1 ? 'Lenders' : 'Lender Information'}>
            {req.Lenders.map((li, i) => {
              const rawAmount = li.LoanAmount
              const amountNum = rawAmount ? Number(rawAmount.replace(/[^\d.]/g, '')) : NaN
              const loanAmount = rawAmount ? (Number.isFinite(amountNum) && amountNum > 0 ? `$${amountNum.toLocaleString()}` : rawAmount) : undefined
              return (
                <Box key={i}>
                  {req.Lenders!.length > 1 && (
                    <Typography variant="overline" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Lender {i + 1}</Typography>
                  )}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '0 24px' }}>
                    <DetailField label="Lender Name" value={li.LenderName} />
                    <DetailField label="Financing Type" value={li.FinancingType} />
                    <DetailField label="Loan Amount" value={loanAmount} />
                    <DetailField label="Loan Number" value={li.LoanNumber} />
                    <DetailField label="Lender Contact" value={[li.LenderFirstName, li.LenderLastName].filter(Boolean).join(' ') || null} />
                    <DetailField label="Lender Email" value={li.LenderEmail} />
                    <DetailField label="Lender Phone" value={li.LenderPhone} />
                  </Box>
                  {i < req.Lenders!.length - 1 && <Divider sx={{ my: 1.5 }} />}
                </Box>
              )
            })}
          </Section>
        )}

        {req.RequestedBy && (
          <Section id="requestedby" title="Requested By">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '0 24px' }}>
              <DetailField label="Role" value={req.RequestedBy.Role} />
              <DetailField label="Name" value={`${req.RequestedBy.FirstName} ${req.RequestedBy.LastName}`} />
              <DetailField label="Email" value={req.RequestedBy.Email} />
              <DetailField label="Phone" value={req.RequestedBy.Phone} />
              <DetailField label="Fax" value={req.RequestedBy.Fax} />
              <DetailField label="Company" value={req.RequestedBy.CompanyName} />
              <DetailFieldFull
                label="Company Address"
                value={[req.RequestedBy.CompanyAddress1, req.RequestedBy.CompanyAddress2, req.RequestedBy.CompanyCity, req.RequestedBy.CompanyState, req.RequestedBy.CompanyZip].filter(Boolean).join(', ') || null}
              />
            </Box>
          </Section>
        )}

        {req.Comments && (
          <Section id="comments" title="Comments">
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{req.Comments}</Typography>
          </Section>
        )}

        {req.Documents.length > 0 && (
          <Section id="documents" title={`Documents (${req.Documents.length})`}>
            {req.Documents.map((doc, i) => (
              <Typography key={i} variant="body2" sx={{ py: 0.5, borderBottom: i < req.Documents.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                {doc.OriginalFileName}
              </Typography>
            ))}
          </Section>
        )}

      </DialogContent>
      <DialogActions>
        {req.Status === 'PENDING' && (
          <Button color="error" onClick={handleCancel} disabled={cancelMutation.isLoading}>
            {cancelMutation.isLoading ? 'Cancelling...' : 'Cancel Request'}
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

const OrderRequestRow = forwardRef<HTMLTableRowElement, OrderRequestRowProps>(({ req, setSelectedRequest, ...tableRowProps }, ref) => {
  const statusColor = STATUS_COLORS[req.Status] ?? 'default'

  return (
    <TableRow
      ref={ref}
      hover
      onClick={() => setSelectedRequest(req)}
      sx={{ cursor: 'pointer' }}
      {...tableRowProps}
    >
      <TableCell>{new Date(req.CreatedAt).toLocaleDateString()}</TableCell>
      <TableCell>{req.OrderType ?? '—'}</TableCell>
      <TableCell>
        {req.PropertyInfo
          ? <><Typography variant="body2">{req.PropertyInfo.Address1}</Typography><Typography variant="caption" color="text.secondary">{req.PropertyInfo.City}, {req.PropertyInfo.State} {req.PropertyInfo.Zip}</Typography></>
          : '—'}
      </TableCell>
      <TableCell>
        {(() => {
          const allParties = [...(req.Parties?.Buyer ?? []), ...(req.Parties?.Seller ?? [])]
          return allParties.length
            ? allParties.map((b, i) => (
              <Fragment key={i}>
                {b?.Primary?.LastName && <Typography variant="body2">{b.Primary.FirstName} {b.Primary.LastName}</Typography>}
                {b?.Spouse?.LastName && <Typography variant="body2">{b.Spouse.FirstName} {b.Spouse.LastName}</Typography>}
              </Fragment>
            ))
            : '—'
        })()}
      </TableCell>
      <TableCell>{getOrderNumberDisplay({orderNum: req.OrderNumber, status: req.Status})}</TableCell>
      <TableCell>
        <Chip label={req.Status} color={statusColor} size="small" />
      </TableCell>
    </TableRow>
  )
})

OrderRequestRow.displayName = 'OrderRequestRow'

export function OrderRequestPage() {
  const [urlParams] = useSearchParams();
  const [selectedRequest, setSelectedRequest] = useState<RetrieveOrderRequestBody | null>(null)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useGetOrderRequests()
  const orderRequests = data?.OrderRequests ?? []
  const submissionSuccess = urlParams.get('success') === 'true';

  useEffect(() => {
    if (submissionSuccess) {
      refetch()
    }
  }, [submissionSuccess, refetch])

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto', mt: '70px', width: '100%'}}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => navigate('/order-requests/new')}>
          New Request
        </Button>
      </Box>

      {submissionSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your title order request has been submitted successfully. A team member will follow up with you shortly.
        </Alert>
      )}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : orderRequests.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No order requests yet.
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={2}>
          {orderRequests.map((req) => {
            const statusColor = STATUS_COLORS[req.Status] ?? 'default'
            const card = (
              <Paper
                key={req.RequestId}
                elevation={3}
                sx={{ p: 2, borderRadius: 3, cursor: 'pointer' }}
                onClick={() => setSelectedRequest(req)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {req.PropertyInfo ? (
                      <>
                        {req.PropertyInfo.Address1}
                        <Typography component="span" variant="caption" color="text.secondary" display="block">
                          {req.PropertyInfo.City}, {req.PropertyInfo.State} {req.PropertyInfo.Zip}
                        </Typography>
                      </>
                    ) : '—'}
                  </Typography>
                  <Chip label={req.Status} color={statusColor} size="small" sx={{ ml: 1, flexShrink: 0 }} />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(req.CreatedAt).toLocaleDateString()}
                  </Typography>
                  {req.OrderType && (
                    <Typography variant="caption" color="text.secondary">
                      {req.OrderType}
                    </Typography>
                  )}
                  {(() => {
                    const allParties = [...(req.Parties?.Buyer ?? []), ...(req.Parties?.Seller ?? [])]
                    return allParties.length ? (
                      <Typography variant="caption" color="text.secondary">
                        {allParties.map((b) => [b.Primary?.FirstName, b.Primary?.LastName].filter(Boolean).join(' ')).filter(Boolean).join(', ')}
                      </Typography>
                    ) : null
                  })()}
                </Box>
              </Paper>
            )
            return req.Status === 'ERROR' ? (
              <Tooltip key={req.RequestId} title="There was an error processing this request. Please contact support." arrow>
                <span>{card}</span>
              </Tooltip>
            ) : card
          })}
        </Stack>
      ) : (
        <TableContainer component={Paper} elevation={8} sx={{ borderRadius: '20px', overflow: 'hidden', width: '100%' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Submitted</TableCell>
                <TableCell>Order Type</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Parties</TableCell>
                <TableCell>File Number</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderRequests.map((req) => (
                req.Status === 'ERROR' ? (
                  <Tooltip key={req.RequestId} title={'There was an error processing this request. Please contact support.'} arrow>
                    <OrderRequestRow req={req} setSelectedRequest={setSelectedRequest} />
                  </Tooltip>
                ) : (
                  <OrderRequestRow key={req.RequestId} req={req} setSelectedRequest={setSelectedRequest} />
                )
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedRequest && (
        <RequestDetailDialog
          req={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onCancel={() => { setSelectedRequest(null); refetch() }}
        />
      )}
    </Box>
  )
}
